import * as React from 'react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@repo/ui';
import { Calendar } from '@repo/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui';
import { useFormContext } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormMessage } from '@repo/ui';
import CalendarIcon from '@repo/ui/components/icons/CalendarIcon';

interface DatePickerProps {
    className?: string;
    name: string;
    label?: string;
    placeholder?: string;
    disabled?: (date: Date) => boolean;
    disabledDate?: boolean;
}

export function DateInputForm({
    className,
    name,
    label,
    placeholder,
    disabled = undefined,
    disabledDate,
}: DatePickerProps) {
    const [calendarOpen, setCalendarOpen] = React.useState(false);
    const { control } = useFormContext();

    // Generate years from 1900 to current year
    const years = React.useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);
    }, []);

    // Generate months
    const months = React.useMemo(
        () => [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ],
        [],
    );

    const [month, setMonth] = React.useState<number>(new Date().getMonth());
    const [year, setYear] = React.useState<number>(new Date().getFullYear());

    const handleMonthChange = (value: string) => {
        setMonth(months.indexOf(value));
    };

    const handleYearChange = (value: string) => {
        setYear(parseInt(value));
    };
    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => (
                <FormItem className={cn('flex flex-col', className)}>
                    <FormLabel className="text-primery-blue-900 font-medium text-[18px] leading-[33.73px] tracking-normal text-start">
                        {label}
                    </FormLabel>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                        <PopoverTrigger asChild disabled={disabledDate}>
                            <Button
                                id="dob"
                                variant={'outline'}
                                className="pe-3 text-start font-medium flex flex-row justify-between"
                            >
                                {field.value ? (
                                    format(field.value, 'yyyy-MM-dd')
                                ) : (
                                    <span className="text-white-blue-600">{placeholder}</span>
                                )}
                                <CalendarIcon width="24" height="24" className="text-white-blue-600" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <div className="flex gap-2 p-3 border-b">
                                <Select value={months[month]} onValueChange={handleMonthChange}>
                                    <SelectTrigger className="w-[140px]">
                                        <SelectValue placeholder="Month" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {months.map((month) => (
                                            <SelectItem key={month} value={month}>
                                                {month}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={year.toString()} onValueChange={handleYearChange}>
                                    <SelectTrigger className="w-[100px]">
                                        <SelectValue placeholder="Year" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {years.map((year) => (
                                            <SelectItem key={year} value={year.toString()}>
                                                {year}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={(newDate) => {
                                    field.onChange(newDate);
                                    setCalendarOpen(false);
                                }}
                                month={new Date(year, month)}
                                onMonthChange={(newMonth) => {
                                    setMonth(newMonth.getMonth());
                                    setYear(newMonth.getFullYear());
                                }}
                                disabled={disabled}
                                initialFocus
                                className={cn(
                                    'p-3 pointer-events-auto rounded-md border-2 shadow-lg bg-card',
                                    'animate-in fade-in-0 zoom-in-95',
                                )}
                            />
                        </PopoverContent>
                    </Popover>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
